import CardSystem from './Card/CardSystem';
import GameObject, { GameObjectEvent } from '../GameObject/GameObject';
import AnimationComponent from '../Component/Animation';
import CardStateHelper, { hasModifierOfType } from '../State/Card/CardStateHelper';
import Renderable from '../Component/Renderable';
import TransformComponent from '../Component/Transform';
import { LevelTask } from 'State/Level/LevelTask';
import { ICoroutine } from './System';
import Point from '../Utility/Point';
import {
    Anim,
    PlayAnimation,
    StartAnimation,
    StartAnimationClip,
    StartAnimationClipAsync,
} from './Animation/AnimationHelper';
import Text from '../Prototype/Text';
import Game from '../Game';
import TextComponent from '../Component/Render/Text';
import Arrow from '../Prototype/Level/Arrow';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { CARD_STACK_OFFSET } from 'Constants/InGame';
import BaseCard from '../Prototype/Level/Card/BaseCard';
import CardComponent from '../Component/Level/Card';
import { CardType } from 'Prototype/Level/Card/CardType';
import { RenderSystemEvent } from './Render/RenderSystem';
import SpriteComponent from '../Component/Render/Sprite';
import GoalMenuTask from './Level/Task/GoalMenuTask';
import { ILevelStateTaskData } from 'State/Level/ILevelState';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { OverlayFadeIn, OverlayFadeOut, TextPopIn } from './Animation/Clip';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { delay } from 'Utility/Task/TaskQueue';
import Interactable from 'Component/Interactable';
import Connected from 'Prototype/Level/Card/Modifier/Connected';
import { LevelHud } from 'System/UI/HUD/LevelHud';
import { Easing } from '@tweenjs/tween.js';
import { generifyPromise } from 'Utility/async';
import Container from 'Prototype/Container';
import { PlayerActions } from 'State/Player/PlayerActions';
import { CardActions } from 'State/Card/CardActions';
import Sprite from 'Prototype/Sprite';

export default class LevelTaskHelper {
    public static routineMap: Map<LevelTask, ICoroutine> = new Map();

    public static handleTask(system: CardSystem, taskData: ILevelStateTaskData): boolean {
        // prettier-ignore
        switch (taskData.task) {
            // LEVEL 1
            case LevelTask.FTUX_CARD_MATCH:
                return this.startRoutine(system, taskData.task, this.cardMatch(system));
            // LEVEL 2
            case LevelTask.FTUX_CARD_DECK:
                return this.startRoutine(system, taskData.task, this.cardDeck(system));

            case LevelTask.FTUX_BOOSTER_UNDO:
                // fixme: move this line (and others) to FTUX.ts
                if (LevelTaskHelper.FTUXUnlocked(FTUXType.UNDO)) return false;
                return this.startRoutine(system, taskData.task, this.undoBooster(system, taskData));

            case LevelTask.FTUX_BOOSTER_JOKER:
                return this.startRoutine(system, taskData.task, this.jokerBooster(system));

            case LevelTask.FTUX_BOOSTER_TNT:
                if (LevelTaskHelper.FTUXUnlocked(FTUXType.TOOLBOX_UNLOCK)) return;
                return this.startRoutine(system, taskData.task, this.toolboxFlow(system, FTUXType.TOOLBOX_UNLOCK, taskData));

            case LevelTask.FTUX_BOOSTER_BULLS_EYE:
                if (LevelTaskHelper.FTUXUnlocked(FTUXType.BULLSEYE_BOOSTER)) return;
                return this.startRoutine(system, taskData.task, this.toolboxFlow(system, FTUXType.BULLSEYE_BOOSTER, taskData));

            case LevelTask.FTUX_BOOSTER_SHUFFLE:
                if (LevelTaskHelper.FTUXUnlocked(FTUXType.SHUFFLE_BOOSTER)) return;
                return this.startRoutine(system, taskData.task, this.toolboxFlow(system, FTUXType.SHUFFLE_BOOSTER, taskData));

            case LevelTask.FTUX_SUITCASE:
                return true;
            case LevelTask.FTUX_HAZARD_BOMB:
                return this.startRoutine(system, taskData.task, this.bombCard(system));
            case LevelTask.FTUX_LOCK:
                return this.startRoutine(system, taskData.task, this.lockCard(system));
            case LevelTask.FTUX_RISE_LOWER:
                return this.startRoutine(system, taskData.task, this.riseLowerCards(system));
            case LevelTask.FTUX_PICKAXE:
                return this.startRoutine(system, taskData.task, this.pickaxeCard(system));
            case LevelTask.FTUX_WATERMELON:
                return this.startRoutine(system, taskData.task, this.watermelonCard(system));
            case LevelTask.FTUX_HARVEST_FESTIVAL:
                return this.startRoutine(system, taskData.task, this.harvestFestivalCard(system));
            case LevelTask.FTUX_SCYTHE:
                return this.startRoutine(system, taskData.task, this.scytheCard(system));
            case LevelTask.FTUX_ICE:
                return this.startRoutine(system, taskData.task, this.iceCard(system));
            case LevelTask.FTUX_ROPE:
                return this.startRoutine(system, taskData.task, this.ropeCard(system));
            case LevelTask.FTUX_TORNADO:
                return this.startRoutine(system, taskData.task, this.tornadoCard(system));
            case LevelTask.FTUX_FOG:
                return this.startRoutine(system, taskData.task, this.fogCard(system));
            case LevelTask.FTUX_CONNECTED_CARDS:
                return this.startRoutine(system, taskData.task, this.connectedCards(system));
            case LevelTask.FTUX_GOLD:
                system.taskQueue.queue(new GoalMenuTask());
                return true;
            case LevelTask.FTUX_TNT_MODIFIER:
                return this.startRoutine(system, taskData.task, this.tntModifier(system));
        }
    }

    private static FTUXUnlocked(type: FTUXType): boolean {
        return FTUXStateHelper.isFtuxCompleted(Game.store.getState().playerState, type);
    }

    private static startRoutine(system: CardSystem, task: LevelTask, iterable: IterableIterator<number>): boolean {
        const routine = LevelTaskHelper.routineMap.get(task);
        if (routine && system.coroutines.indexOf(routine) !== -1) return false;

        LevelTaskHelper.routineMap.set(task, system.startCoroutine(iterable));
        return true;
    }

    private static *overlayTutorial(
        system: CardSystem,
        type: CardType | CardModifierType,
        text: string
    ): IterableIterator<number> {
        // Yield until level is considered active to ensure the cards in cardsState.inPlay are up to date
        while (!system.LevelActiveHandled) {
            yield* delay(25);
        }

        const textObject = GameObject.Instantiate(Text, {
            text,
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 35,
                wordWrapWidth: 1000,
                wordWrap: true,
                align: 'center',
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(0, 0),
            relativePosition: new Point(0, 0.34),
        });
        textObject.addComponent(AnimationComponent);

        const cardsState = Game.store.getState().levelState.cardsState;

        const cards: BaseCard[] = [];
        cardsState.inPlay.map(id => {
            if (Object.values(CardType).includes(type as CardType)) {
                if (cardsState.cards[id].type === type) {
                    cards.push(system.getCardByID(id));
                }
            } else {
                const t = type === CardModifierType.lowering ? CardModifierType.rising : type;

                if (hasModifierOfType(cardsState, id, t as CardModifierType)) {
                    cards.push(system.getCardByID(id));
                }

                if (hasModifierOfType(cardsState, id, type as CardModifierType)) {
                    cards.push(system.getCardByID(id));
                }
            }
        });

        let selectedCard: BaseCard = cards.find(card => card.getComponent(CardComponent).faceUp);

        while (!selectedCard) {
            yield;
            selectedCard = cards.find(card => card.getComponent(CardComponent).faceUp);
        }

        while (system.taskQueue.isRunning()) {
            yield* delay(25);
        }

        const overlay = LevelHud.instance.getLayout().overlay as GameObject<PIXI.Graphics>;
        overlay.addChild(textObject);
        const overlayRenderable: Renderable = overlay.getComponent<Renderable>(Renderable);
        overlayRenderable.alpha = 0;
        overlayRenderable.visible = true;

        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, overlay.parent);

        const selectedCardComponent: CardComponent = selectedCard.getComponent(CardComponent);
        const cardInteractable: Interactable = selectedCard.getComponent(Interactable);

        cardInteractable.enabled = false;
        cardInteractable.buttonMode = false;

        const cardParent = selectedCard.parent;

        yield* delay(500);

        let redGlow;
        let failCharacter;
        if (type === CardModifierType.tnt) {
            redGlow = GameObject.Instantiate(Sprite, {
                name: 'tnt_red_glow',
                anchor: new Point(0.5, 0.5),
                texture: 'card_red_glow',
                scale: new Point(1.05, 1.05),
                alpha: 0.6,
            });
            selectedCard.addChild(redGlow);
            overlay.addChild(redGlow);

            failCharacter = GameObject.Instantiate(Sprite, {
                name: 'fail_character',
                texture: 'UI_TNT_Tutorial_Character',
                anchor: new Point(0.5, 1),
                position: new Point(0, 0),
                relativePosition: new Point(-0.35, 0.5),
                alpha: 0,
            });
            overlay.addChild(failCharacter);
            textObject.getComponent(TransformComponent).scale = new Point(0);
            overlay.addChild(textObject);
        }

        overlay.addChild(selectedCard);
        const connectedModifier = selectedCard.getModifier(Connected);

        if (connectedModifier) {
            const cardState = cardsState.cards[selectedCard.id];
            const modifierState = cardState.modifiers.find(m => m.type === CardModifierType.connected);
            const otherCard = system.getCardByID(modifierState.properties.cardID);

            otherCard && overlay.addChild(otherCard);
            overlay.addChild(connectedModifier.rope);
        }

        StartAnimationClip(new OverlayFadeIn(overlay, { alpha: 1 }));
        selectedCardComponent.animating = true;

        if (type === CardModifierType.tnt) {
            yield* delay(500);

            StartAnimationClip(new TextPopIn(textObject));

            Anim(failCharacter, {
                alpha: 1,
                duration: 400,
                delay: 0,
            });
        }

        yield* delay(1000);

        let endTutorial = false;
        overlay.once(GameObjectEvent.SELECTED, () => {
            endTutorial = true;
        });

        while (!endTutorial) {
            yield;
        }

        if (!selectedCard.destroyed) {
            cardParent.addChild(selectedCard);
        }

        if (type === CardModifierType.tnt) {
            failCharacter.destroy();
            redGlow.destroy();
        }

        selectedCardComponent.animating = false;
        cardInteractable.enabled = true;
        cardInteractable.buttonMode = true;
        system.sortCardsInPlay(system.store.getState().levelState.cardsState);
        yield* PlayAnimation(new OverlayFadeOut(overlay));
        textObject.destroy();

        while (true) {
            yield;
        }
    }

    private static *cardMatch(system: CardSystem): IterableIterator<number> {
        const arrow = GameObject.Instantiate(Arrow);

        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Tutorial1'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 35,
                wordWrapWidth: 950,
                align: 'center',
            },
            align: 'center',
            anchor: new Point(0.5, 0.5),
            position: new Point(0, 150),
            relativePosition: new Point(0.5, 0.5),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        const arrowTransform = arrow.getComponent(TransformComponent);

        let currentCard: GameObject;
        let targetPosition = new Point();
        let targetRotation = 0;

        yield* delay(200);

        system.animator.addChild(text);
        system.cardsContainer.addChild(arrow);
        LevelTaskHelper.addOverlay(system);

        while (true) {
            // prettier-ignore
            const matches = CardStateHelper.getCardMatches(system.store.getState());
            system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, arrow);
            // No matches left - hide arrow sprite and end co-routine.
            if (!matches[0]) {
                arrow.getComponent(Renderable).visible = false;
                text.getComponent(TextComponent).visible = false;
                system.animator.getChildByName('overlay').getComponent(Renderable).alpha = 0;
                system.animator.getChildByName('overlay').getComponent(Renderable).visible = false;
                system.systemContainer.addChild(system.stackOuterContainer);
                // Fix relative positions after re-parenting.
                system.stackOuterContainer.getComponent(TransformComponent).position.set(0, 0);

                // Re-add animator on top.
                system.systemContainer.addChild(system.animator);
                break;
            }

            // prettier-ignore
            const card = system.getCardByID(matches[0]);

            if (card && currentCard !== card) {
                // Cache transform.
                const targetTransform = card.getComponent(TransformComponent);

                targetRotation = targetTransform.rotation;

                // prettier-ignore
                targetPosition.set(
                    targetTransform.position.x - Math.cos(targetRotation + Math.PI * 0.5) * 200,
                    targetTransform.position.y - Math.sin(targetRotation + Math.PI * 0.5) * 200
                );

                // First card.
                if (!currentCard) {
                    arrowTransform.position.copy(targetPosition);
                    arrowTransform.rotation = targetRotation;
                }

                currentCard = card;
            }

            // prettier-ignore
            arrowTransform.position.x += (targetPosition.x - arrowTransform.position.x) * 0.2;
            // prettier-ignore
            arrowTransform.position.y += (targetPosition.y - arrowTransform.position.y) * 0.2;
            // prettier-ignore
            arrowTransform.rotation += (targetRotation - arrowTransform.rotation) * 0.2;

            yield;
        }
    }

    private static *cardDeck(system: CardSystem): IterableIterator<number> {
        const textDeck = createText();
        const { arrow, arrowTransform, arrowRenderable, arrowSprite } = LevelTaskHelper.createArrow(system);

        let currentCard: GameObject;
        let targetPosition = new Point();
        let targetRotation = 0;

        let deckArrowShown: boolean = false;
        let cardMatchArrowShown: boolean = false;
        let matchNotFound: boolean = true;
        while (true) {
            // prettier-ignore
            const matches = CardStateHelper.getCardMatches(system.store.getState());
            const someCardsInPlayRemain = system.getCardsInPlay().length > 0;
            const someCardsInFoldedExist = system.getCardsFolded().length > 0;

            if (matches.length === 0 && someCardsInPlayRemain && someCardsInFoldedExist) {
                showArrowOnDeck();
            } else if (deckArrowShown && matchNotFound) {
                deckArrowShown = false;
                matchNotFound = false;
                StartAnimation(textDeck, 'TextPopOut');
            } else if (!deckArrowShown && !matchNotFound && !cardMatchArrowShown && !currentCard) {
                system.cardsContainer.addChild(arrow);

                // prettier-ignore
                const card = system.getCardByID(matches[0]);

                if (card && currentCard !== card) {
                    // Cache transform.
                    const targetTransform = card.getComponent(TransformComponent);

                    targetRotation = targetTransform.rotation;

                    // prettier-ignore
                    targetPosition.set(
                        targetTransform.position.x - Math.cos(targetRotation + Math.PI * 0.5) * 200,
                        targetTransform.position.y - Math.sin(targetRotation + Math.PI * 0.5) * 200
                    );

                    // First card.
                    if (!currentCard) {
                        arrowTransform.position.copy(targetPosition);
                        arrowTransform.rotation = targetRotation;
                    }

                    currentCard = card;
                }

                cardMatchArrowShown = true;
            } else if (cardMatchArrowShown && matches.length === 1) {
                cardMatchArrowShown = false;
                arrowRenderable.visible = false;
                const overlay = system.animator.getChildByName('overlay');
                StartAnimation(overlay, 'OverlayFadeOut');
            }

            // prettier-ignore
            arrowTransform.position.x += (targetPosition.x - arrowTransform.position.x) * 0.2;
            // prettier-ignore
            arrowTransform.position.y += (targetPosition.y - arrowTransform.position.y) * 0.2;

            const cardsInStack = system.getCardsInStack();
            if (cardsInStack.length === 0) {
                arrowRenderable.visible = false;
            }

            const cardsInPlay = system.getCardsInPlay();
            if (cardsInPlay.length === 0) {
                arrowRenderable.visible = false;

                system.systemContainer.addChild(system.stackOuterContainer);

                system.stackOuterContainer.getComponent(TransformComponent).position.set(0, 0);

                break;
            }

            yield;
        }

        return;
        function showArrowOnDeck() {
            arrowTransform.position.set(CARD_STACK_OFFSET + CARD_STACK_OFFSET / 2 + arrowSprite.width / 4, -200);

            // Force bring to front to fix stack layering issues
            system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, arrow);
            if (!deckArrowShown) {
                StartAnimation(arrow, 'Alpha', {
                    duration: 250,
                    startAlpha: 0,
                    endAlpha: 1,
                });
                StartAnimation(textDeck, 'TextPopIn');
                LevelTaskHelper.addOverlay(system);
                deckArrowShown = true;
            }
        }
        function createText(): Text {
            const text = GameObject.Instantiate(Text, {
                text: Game.locale.getText('FTUX_3'),
                style: {
                    ...Game.style.getStyle('ftux.text'),
                    fontSize: '100px',
                    align: 'center',
                },
                bounds: new Point(380, 188),
                anchor: new Point(0.5, 1),
                position: new Point(0, 0),
                relativePosition: new Point(0.25, 0.72),
            });
            text.getComponent(TextComponent).alpha = 0;
            text.addComponent(AnimationComponent);
            system.animator.addChild(text);
            return text;
        }
    }

    private static createArrow(
        system: CardSystem
    ): {
        arrow: Arrow;
        arrowTransform: TransformComponent;
        arrowRenderable: Renderable;
        arrowSprite: SpriteComponent;
    } {
        const arrow = GameObject.Instantiate(Arrow);
        system.stackContainer.addChild(arrow);

        const arrowTransform = arrow.getComponent(TransformComponent);
        const arrowRenderable: Renderable = arrow.getComponent(Renderable);
        const arrowSprite: SpriteComponent = arrow.getComponentsInChildren(SpriteComponent)[0];
        arrowRenderable.visible = false;

        return { arrow, arrowTransform, arrowRenderable, arrowSprite };
    }

    private static *undoBooster(system: CardSystem, taskData: ILevelStateTaskData): IterableIterator<number> {
        // yield until a match has been made or a card has been drawn
        while (true) {
            if (system.getCardsFolded().length > 1) break;
            yield;
        }

        LevelTaskHelper.addOverlay(system);

        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('TutorialUndoBooster'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 32,
                wordWrapWidth: 325,
                align: 'center',
            },
            anchor: new Point(0.5, 0.5),
            position: new Point(0, 0),
            relativePosition: new Point(0.84, 0.88),
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });

        system.animator.addChild(text);
        system.scene.emit('UndoReward');

        yield* delay(1000);

        const overlay = system.animator.getChildByName('overlay');
        overlay.getComponent(Interactable).enabled = true;
        let clicked = false;

        const hideOverlay = () => {
            clicked = true;
        };

        overlay.once(GameObjectEvent.SELECTED, hideOverlay);
        Game.store.waitFor(CardActions.playCard).then(hideOverlay);

        while (!clicked) yield;

        overlay.off(GameObjectEvent.SELECTED, hideOverlay);

        overlay.getComponent(Interactable).enabled = false;
        text.getComponent(TextComponent).visible = false;
        StartAnimationClip(new OverlayFadeOut(overlay));

        while (true) yield;
    }

    private static *jokerBooster(system: CardSystem): IterableIterator<number> {
        function* cardFolded(): IterableIterator<number> {
            const foldedLength = system.getCardsFolded().length;
            while (foldedLength === system.getCardsFolded().length) yield;
        }

        const layout = LevelHud.instance.getLayout();
        const overlay = layout.overlay;
        const jokerBtn = layout.jokerButton;
        const jokerContainer = layout.jokerContainer;
        const jokerParent = jokerContainer.parent;
        overlay.addChild(jokerContainer);

        yield* cardFolded();
        yield* cardFolded();

        jokerContainer.getComponent(Renderable).visible = true;
        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, overlay.parent);
        StartAnimationClip(new OverlayFadeIn(overlay));

        jokerBtn.container.once('pointertap', () => {
            jokerBtn.emit(GameObjectEvent.SELECTED);
        });

        const jokerTransform = jokerContainer.getComponent(TransformComponent);

        const text = GameObject.Instantiate(Text, {
            text: Game.locale.getText('TutorialJokerBooster'),
            style: {
                ...Game.style.getStyle('ftux.text'),
                fontSize: 32,
                wordWrapWidth: 900,
                align: 'center',
            },
            bounds: [140, 240],
            anchor: new Point(0.5, 1),
            x: jokerTransform.x,
            y: jokerTransform.y - 350,
        });
        text.getComponent(TextComponent).alpha = 0;
        text.addComponent(AnimationComponent, {
            playOnStart: true,
            clipName: 'TextPopIn',
        });
        overlay.addChild(text);

        const showArrowOn = (gameObject: GameObject, offsetY: number): GameObject => {
            const { arrow, arrowTransform, arrowRenderable } = LevelTaskHelper.createArrow(system);
            arrowRenderable.visible = true;
            overlay.addChild(arrow);

            const goTransform = gameObject.getComponent(TransformComponent);
            arrowTransform.position.set(goTransform.x, goTransform.y + offsetY);
            Anim(arrow, { y: '+50', duration: 300, easing: Easing.Sinusoidal.InOut, repeatCount: -1 });

            return arrow;
        };

        const jokerButtonArrow = showArrowOn(jokerContainer, -340);

        yield* cardFolded();

        text.destroy();
        jokerButtonArrow.destroy();
        jokerParent.addChild(jokerContainer);

        const tmpStack = GameObject.Instantiate(Container);
        system.stackContainer.addChild(tmpStack);
        overlay.addChild(tmpStack);

        const foldedCards = system.getCardsFolded();
        const jokerCard = foldedCards[foldedCards.length - 1];
        jokerCard.getComponent(Interactable).enabled = false;
        tmpStack.addChild(jokerCard);

        const faceUpCards = system.getCardsInPlay().filter(c => c.getComponent(CardComponent).faceUp);
        const cardArrows: GameObject[] = [];

        faceUpCards.forEach(card => {
            overlay.addChild(card);
            const arrow = showArrowOn(card, -270);
            cardArrows.push(arrow);
        });

        yield* cardFolded();

        system.sortCardsInPlay(system.store.getState().levelState.cardsState);
        system.cardStack.sort();

        cardArrows.forEach(arrow => {
            arrow.destroy();
        });

        yield* generifyPromise(StartAnimationClipAsync(new OverlayFadeOut(overlay)));

        system.store.dispatch(
            PlayerActions.setPlayerState(
                FTUXStateHelper.updateFTUXProgress(system.store.getState().playerState, [FTUXType.JOKER])
            )
        );

        while (true) yield;
    }

    private static *tntModifier(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialTntModifier');
        yield* this.overlayTutorial(system, CardModifierType.tnt, text);
    }

    private static *bombCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialTomatoBomb');
        yield* this.overlayTutorial(system, CardModifierType.bomb, text);
    }

    private static *lockCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialLockCard');
        yield* this.overlayTutorial(system, CardType.lock, text);
    }

    private static *riseLowerCards(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialRiseLowerCards');
        yield* this.overlayTutorial(system, CardModifierType.lowering, text);
    }

    private static *pickaxeCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialPickaxe');
        yield* this.overlayTutorial(system, CardType.rock, text);
    }

    private static *watermelonCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('HelperCard') + Game.locale.getText('TutorialWatermelon');
        yield* this.overlayTutorial(system, CardType.boom, text);
    }

    private static *scytheCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('HelperCard') + Game.locale.getText('TutorialScythe');
        yield* this.overlayTutorial(system, CardType.zap, text);
    }

    private static *iceCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialIce');
        yield* this.overlayTutorial(system, CardModifierType.ice, text);
    }

    private static *ropeCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialRope');
        yield* this.overlayTutorial(system, CardModifierType.rope, text);
    }

    private static *tornadoCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialTornado');
        yield* this.overlayTutorial(system, CardType.tornado, text);
    }

    private static *fogCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialFog');
        yield* this.overlayTutorial(system, CardModifierType.fog, text);
    }

    private static *connectedCards(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('TutorialConnectedCard');
        yield* this.overlayTutorial(system, CardModifierType.connected, text);
    }

    private static *harvestFestivalCard(system: CardSystem): IterableIterator<number> {
        const text = Game.locale.getText('HelperCard') + Game.locale.getText('TutorialHarvestFestival');
        yield* this.overlayTutorial(system, CardType.plus_three, text);
    }

    private static *toolboxFlow(
        system: CardSystem,
        type: FTUXType,
        taskData: ILevelStateTaskData
    ): IterableIterator<number> {
        system.scene.emit('ToolboxFTUX', { type, taskData });
    }

    private static addOverlay(system: CardSystem): void {
        system.animator.addChild(system.cardsContainer);
        system.animator.addChild(system.stackOuterContainer);

        // Fix relative positions after re-parenting.
        system.cardsContainer.getComponent(TransformComponent).position.set(0, 0);

        system.stackOuterContainer.getComponent(TransformComponent).position.set(0, 0);

        const overlay = system.animator.getChildByName('overlay');

        StartAnimation(overlay, 'OverlayFadeIn');
    }
}
